import { INTEGRATION_PROVIDERS } from '@vy/types';
import { raise, raiseUnsupported, validateNotNullOrUndefined } from '@vy/utils';
import {
  INTEGRATION_TYPES,
  INVOICE_NUMBER_SYNC_STRATEGY,
  INVOICE_PAYMENT_STATUS_SYNC_STRATEGY,
  TAX_EXPORT_STRATEGY,
  XeroIntegrationSettings,
  XeroIntegrationSettingsSchema,
} from '@vy/integrations-types';
import { useMemo } from 'react';
import { useIntegrationSettings } from '../../../../../../../hooks/requests/use-integration-settings';
import type { XeroConfigurationFormValues } from '../XeroConfigurationFormSchema';
import { SYNC_DIRECTIONS } from '../XeroConfigurationFormSchema';

type FetchXeroSettingsResult = {
  integrationId: string;
  configuration: XeroConfigurationFormValues;
};

export function useFetchXeroSettings(): FetchXeroSettingsResult {
  const integrationSettingsResponse = useIntegrationSettings(INTEGRATION_PROVIDERS.xero);
  const xeroIntegrationSettings = integrationSettingsResponse.data;

  validateNotNullOrUndefined(xeroIntegrationSettings, 'xeroIntegrationSettings');

  const parsedXeroIntegrationSettings = XeroIntegrationSettingsSchema.parse(
    xeroIntegrationSettings.integrationSettings,
  );

  const mappedSettings = mapIntegrationSettingsToFormValues(parsedXeroIntegrationSettings);

  return useMemo(
    () => ({
      integrationId: xeroIntegrationSettings.integrationId,
      configuration: mappedSettings,
    }),
    [xeroIntegrationSettings.integrationId, mappedSettings],
  );
}

function mapIntegrationSettingsToFormValues(
  integrationSettings: XeroIntegrationSettings,
): XeroConfigurationFormValues {
  if (integrationSettings.integrationProvider !== INTEGRATION_TYPES.XERO) {
    raiseUnsupported(integrationSettings.integrationProvider);
  }

  const config = integrationSettings.integrationConfiguration;

  if (!config) {
    raise('Integration configuration is missing');
  }

  const authConfig = config.authConfig;
  const autoExportInvoices = !!config.generalConfig?.autoExportInvoices;
  const autoImportCustomers = config.generalConfig?.autoImportCustomers ?? false;
  const invoiceNumberSyncStrategy =
    config.generalConfig?.invoiceNumberSyncStrategy ?? INVOICE_NUMBER_SYNC_STRATEGY.KEEP_SEPARATE;
  const invoicePaymentStatusSyncStrategy =
    config.generalConfig?.invoicePaymentStatusSyncStrategy ??
    INVOICE_PAYMENT_STATUS_SYNC_STRATEGY.KEEP_SEPARATE;

  const syncCustomerDirection = autoImportCustomers
    ? SYNC_DIRECTIONS.SYNC_TO_VAYU
    : SYNC_DIRECTIONS.AUTO_SYNC_DISABLED;

  validateNotNullOrUndefined(authConfig, 'authConfig');

  const taxExportStrategy =
    config.generalConfig?.taxExportStrategy ?? TAX_EXPORT_STRATEGY.INCLUDE_IN_AMOUNT;
  const accountTaxItem = config.generalConfig?.accountTaxItem;

  return {
    syncCustomer: syncCustomerDirection,
    syncApprovedInvoices: autoExportInvoices,
    autoExportCreditMemos: config.generalConfig?.autoExportCreditMemos ?? false,
    invoiceNumberSyncStrategy,
    invoicePaymentStatusSyncStrategy,
    defaultStripeDepositToAccountId: config.generalConfig?.defaultStripeDepositToAccountId ?? null,
    defaultDepositToAccountId: config.generalConfig?.defaultDepositToAccountId ?? null,
    taxExportStrategy,
    accountTaxItem,
  };
}
